import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, 
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Alert, CircularProgress, Divider, Badge, Paper
} from '@mui/material';
import {
  Videocam as CameraIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  RecordVoiceOver as RecordIcon,
  PhotoCamera as SnapshotIcon,
  CloudDownload as DownloadIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateIcon,
  NightMode as NightModeIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';
import moment from 'moment';

const CameraManagement = () => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'live', 'settings', 'recordings'
  const [liveStreams, setLiveStreams] = useState({});

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/unifi/protect/cameras');
      setCameras(response.data);
    } catch (error) {
      console.error('Error fetching cameras:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartLiveStream = async (cameraId) => {
    try {
      const response = await axiosInstance.post('/api/unifi/protect/stream/start', {
        cameraId
      });
      setLiveStreams(prev => ({
        ...prev,
        [cameraId]: response.data.streamUrl
      }));
      showSnackbar('Transmisión iniciada', 'success');
    } catch (error) {
      console.error('Error starting stream:', error);
      showSnackbar('Error al iniciar transmisión', 'error');
    }
  };

  const handleStopLiveStream = async (cameraId) => {
    try {
      await axiosInstance.post('/api/unifi/protect/stream/stop', {
        cameraId
      });
      setLiveStreams(prev => {
        const newStreams = { ...prev };
        delete newStreams[cameraId];
        return newStreams;
      });
      showSnackbar('Transmisión detenida', 'success');
    } catch (error) {
      console.error('Error stopping stream:', error);
      showSnackbar('Error al detener transmisión', 'error');
    }
  };

  const handleTakeSnapshot = async (cameraId) => {
    try {
      const response = await axiosInstance.post('/api/unifi/protect/snapshot', {
        cameraId
      });
      // Download the snapshot
      const link = document.createElement('a');
      link.href = response.data.snapshotUrl;
      link.download = `snapshot_${cameraId}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.jpg`;
      link.click();
      showSnackbar('Captura guardada', 'success');
    } catch (error) {
      console.error('Error taking snapshot:', error);
      showSnackbar('Error al tomar captura', 'error');
    }
  };

  const handlePTZControl = async (cameraId, action) => {
    try {
      await axiosInstance.post('/api/unifi/protect/ptz', {
        cameraId,
        action
      });
      showSnackbar(`Control PTZ: ${action}`, 'success');
    } catch (error) {
      console.error('Error PTZ control:', error);
      showSnackbar('Error en control PTZ', 'error');
    }
  };

  const handleOpenDialog = (type, camera = null) => {
    setDialogType(type);
    setSelectedCamera(camera);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedCamera(null);
    setDialogType('');
  };

  const showSnackbar = (message, severity = 'success') => {
    console.log(`${severity}: ${message}`);
  };

  const getCameraStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      case 'recording': return 'warning';
      default: return 'default';
    }
  };

  const getCameraStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'recording': return 'Grabando';
      default: return 'Desconocido';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Gestión de Cámaras
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchCameras}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<CameraIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Agregar Cámara
          </Button>
        </Box>
      </Box>

      {/* Cameras Grid */}
      <Grid container spacing={3}>
        {cameras.map((camera) => (
          <Grid item xs={12} md={6} lg={4} key={camera.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CameraIcon color="primary" />
                    <Typography variant="h6" component="h3">
                      {camera.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={getCameraStatusText(camera.status)}
                    color={getCameraStatusColor(camera.status)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {camera.location}
                </Typography>

                {/* Camera Preview */}
                <Box sx={{ 
                  width: '100%', 
                  height: 150, 
                  bgcolor: 'grey.200', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  position: 'relative'
                }}>
                  {liveStreams[camera.id] ? (
                    <img 
                      src={liveStreams[camera.id]} 
                      alt={camera.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin transmisión
                    </Typography>
                  )}
                  
                  {/* Recording indicator */}
                  {camera.status === 'recording' && (
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <RecordIcon sx={{ fontSize: 8 }} />
                    </Box>
                  )}
                </Box>

                {/* Camera Controls */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={liveStreams[camera.id] ? <StopIcon /> : <PlayIcon />}
                    onClick={() => liveStreams[camera.id] 
                      ? handleStopLiveStream(camera.id)
                      : handleStartLiveStream(camera.id)
                    }
                    disabled={camera.status === 'offline'}
                  >
                    {liveStreams[camera.id] ? 'Detener' : 'En Vivo'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SnapshotIcon />}
                    onClick={() => handleTakeSnapshot(camera.id)}
                    disabled={camera.status === 'offline'}
                  >
                    Captura
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FullscreenIcon />}
                    onClick={() => handleOpenDialog('live', camera)}
                    disabled={camera.status === 'offline'}
                  >
                    Pantalla Completa
                  </Button>
                </Box>

                {/* PTZ Controls */}
                {camera.ptz && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePTZControl(camera.id, 'up')}
                      disabled={camera.status === 'offline'}
                    >
                      <ZoomInIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePTZControl(camera.id, 'down')}
                      disabled={camera.status === 'offline'}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePTZControl(camera.id, 'left')}
                      disabled={camera.status === 'offline'}
                    >
                      <RotateIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePTZControl(camera.id, 'right')}
                      disabled={camera.status === 'offline'}
                    >
                      <RotateIcon sx={{ transform: 'scaleX(-1)' }} />
                    </IconButton>
                  </Box>
                )}

                {/* Camera Info */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Resolución: {camera.resolution}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      FPS: {camera.fps}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleOpenDialog('settings', camera)}>
                      <SettingsIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog('recordings', camera)}>
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Live Stream Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'live'} 
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Transmisión en Vivo - {selectedCamera?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            bgcolor: 'black',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {selectedCamera && liveStreams[selectedCamera.id] ? (
              <img 
                src={liveStreams[selectedCamera.id]} 
                alt={selectedCamera.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="body2" color="white">
                Iniciando transmisión...
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'settings'} 
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configuración - {selectedCamera?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Nombre"
                defaultValue={selectedCamera?.name}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Ubicación"
                defaultValue={selectedCamera?.location}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Resolución</InputLabel>
                <Select defaultValue={selectedCamera?.resolution}>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                  <MenuItem value="4K">4K</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>FPS</InputLabel>
                <Select defaultValue={selectedCamera?.fps}>
                  <MenuItem value={15}>15 FPS</MenuItem>
                  <MenuItem value={30}>30 FPS</MenuItem>
                  <MenuItem value={60}>60 FPS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Grabación automática</Typography>
                <Switch defaultChecked={selectedCamera?.autoRecord} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Modo nocturno</Typography>
                <Switch defaultChecked={selectedCamera?.nightMode} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Recordings Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'recordings'} 
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Grabaciones - {selectedCamera?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Las grabaciones se pueden descargar desde el sistema UniFi Protect
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Esta función se integrará con el sistema de grabaciones de UniFi Protect
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CameraManagement; 